import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import axios from "../utils/axios";
import toast from "react-hot-toast";

const ReviewModal = ({ product, orderId, existingReview = null, onClose }) => {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState(
    existingReview?.review_text || ""
  );

  const submitReviewMutation = useMutation(
    async (reviewData) => {
      if (existingReview) {
        // Update existing review
        const response = await axios.put(
          `/api/reviews/${existingReview.id}`,
          reviewData
        );
        return response.data;
      } else {
        // Submit new review
        const response = await axios.post("/api/reviews", reviewData);
        return response.data;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("customer-orders");
        queryClient.invalidateQueries(["product", product.product_id]);
        queryClient.invalidateQueries("products");
        toast.success(
          existingReview
            ? "Review updated successfully!"
            : "Review submitted successfully!"
        );
        onClose();
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || existingReview
            ? "Failed to update review"
            : "Failed to submit review"
        );
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    submitReviewMutation.mutate({
      product_id: product.product_id,
      order_id: orderId,
      rating,
      review_text: reviewText.trim(),
    });
  };

  const StarRating = () => {
    return (
      <div className="flex items-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <svg
              className={`w-10 h-10 ${
                star <= (hoverRating || rating)
                  ? "text-yellow-400 fill-current"
                  : "text-gray-300"
              }`}
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {existingReview ? "Edit Your Review" : "Write a Review"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Product Info */}
          <div className="bg-gray-50 p-4 rounded-lg flex items-center space-x-4">
            {product.product_image && (
              <img
                src={product.product_image}
                alt={product.product_name}
                className="w-20 h-20 object-cover rounded"
              />
            )}
            <div>
              <h3 className="font-semibold text-gray-900">
                {product.product_name}
              </h3>
              <p className="text-sm text-gray-600">
                Order placed on{" "}
                {new Date(product.order_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Rating Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Your Rating *
            </label>
            <StarRating />
            {rating > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                {rating === 1 && "Poor"}
                {rating === 2 && "Fair"}
                {rating === 3 && "Good"}
                {rating === 4 && "Very Good"}
                {rating === 5 && "Excellent"}
              </p>
            )}
          </div>

          {/* Review Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Review
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows="6"
              className="input"
              placeholder="Share your experience with this product. What did you like or dislike? How did it help you?"
            />
            <p className="text-xs text-gray-500 mt-1">
              Optional - Write at least 10 characters for a helpful review
            </p>
          </div>

          {/* Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">
              Tips for writing a great review:
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Be specific about what you liked or didn't like</li>
              <li>
                • Mention how the product met (or didn't meet) your expectations
              </li>
              <li>• Share details about quality, value, and performance</li>
              <li>• Keep it honest and helpful for other customers</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary btn-md"
              disabled={submitReviewMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-md"
              disabled={submitReviewMutation.isLoading || rating === 0}
            >
              {submitReviewMutation.isLoading ? (
                <div className="flex items-center">
                  <div className="loading-dots mr-2">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  {existingReview ? "Updating..." : "Submitting..."}
                </div>
              ) : existingReview ? (
                "Update Review"
              ) : (
                "Submit Review"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;
